import styles from './Shift.module.css';

import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import History from '@mui/icons-material/History';
import InfoToolTip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import Bell from '@mui/icons-material/NotificationsActive';
import Calendar from '@mui/icons-material/CalendarToday';
import Check from '@mui/icons-material/CheckCircle';
import Clock from '@mui/icons-material/AccessTime';
import Downhill from '@mui/icons-material/DownhillSkiing';
import Exclamation from '@mui/icons-material/ErrorOutline';
import Hourglass from '@mui/icons-material/HourglassTop';

import React from 'react';

import { DateTime } from 'luxon';
import { useInterval } from '../../hooks/useInterval';
import { Roster } from '../Roster/Roster';
import { Roles } from '../Roles/Roles';
import { Timelog } from '../Timelog/Timelog';

const Alert = ({
    shift,
    next,
    assigned,
    available,
    late,
    started,
    stopped,
    onAssignDuty,
    onShowTimelog,
    patroller
}) => {
    const [ shiftToday, setShiftToday ] = React.useState(null);
    const [ shiftUpcoming, setShiftUpcoming ] = React.useState(null);
    const [ oldShift, setOldShift ] = React.useState(null);
    const [ missed, setMissed ] = React.useState(null);

    const addShift = !assigned && (available || (shift.event?.allowUnscheduled && (shift.event?.allowUnscheduledAfter ? shift.available === 0 : true)));

    const currentTimelog = shift.timelogs?.find(t => t.patroller === patroller?.id);
    const shiftActive = currentTimelog?.started && !currentTimelog?.stopped;
    const shiftCompleted = currentTimelog?.started && currentTimelog?.stopped;

    const handleOnAssignDuty = (e) => {
        e.stopPropagation();

        onAssignDuty(shift);
    };

    const handleShowTimelog = (e) => {
        e.stopPropagation();

        onShowTimelog(shift);
    };

    useInterval(() => {
        const now = DateTime.local();
        const today = now.toISODate();
        const shiftStartDate = shift?.start && DateTime.fromMillis(shift.start).toISODate();
        const shiftEndDate = shift?.end && DateTime.fromMillis(shift.end).toISODate();

        setShiftToday((next || assigned) && shiftStartDate === today && now < DateTime.fromMillis(shift.start));
        setMissed(assigned && shiftStartDate <= today && today >= shiftEndDate && (!stopped && !started));
        setShiftUpcoming((next || assigned) && shiftStartDate > today);
        setOldShift(shiftStartDate < today);
    }, 500);

    // Original Shifts context logic (when shiftStatus is null/undefined)
    return (
        addShift ? (
            <IconButton onClick={ handleOnAssignDuty } sx={ { p: 0 } }><AddIcon fontSize="small" /></IconButton>
        ) : shiftUpcoming ? (
            <IconButton onClick={ handleOnAssignDuty } sx={ { p: 0 } }><Hourglass fontSize="small" /></IconButton>
        ) : shiftActive && !late ? (
            <IconButton onClick={ handleShowTimelog } sx={ { p: 0 } }><Clock fontSize="small" /></IconButton>
        ) : shiftCompleted ? (
            <IconButton onClick={ handleShowTimelog } sx={ { p: 0 } }><Check fontSize="small" /></IconButton>
        ) : late ? (
            <IconButton onClick={ handleOnAssignDuty } sx={ { p: 0 } }><Bell fontSize="small" /></IconButton>
        ) : shiftToday ? (
            <IconButton onClick={ handleOnAssignDuty } sx={ { p: 0 } }><Downhill fontSize="small" /></IconButton>
        ) : missed ? (
            <IconButton onClick={ handleShowTimelog } sx={ { p: 0 } }><Exclamation fontSize="small" /></IconButton>
        ) : oldShift ? (
            <IconButton sx={ { p: 0 } }><History fontSize="small" /></IconButton>
        ) : shiftActive || shiftCompleted ? (
            <IconButton onClick={ handleShowTimelog } sx={ { p: 0 } }><Calendar fontSize="small" /></IconButton>
        ) : (
            <IconButton sx={ { p: 0 } }></IconButton>
        )
    );
};

export const Shift = ({ shift, patroller, assigned, nextDuty, shiftStatus, onAssignDuty }) => {
    const [ showRoster, setShowRoster ] = React.useState(false);
    const [ showRole, setShowRole ] = React.useState(false);
    const [ showTimelog, setShowTimelog ] = React.useState(false);
    const [ shiftAvailable, setShiftAvailable ] = React.useState(null);
    const [ shiftDisabled, setShiftDisabled ] = React.useState(null);
    const [ shiftNext, setShiftNext ] = React.useState(null);
    const [ shiftLate, setShiftLate ] = React.useState(null);
    const [ shiftCompleted, setShiftCompleted ] = React.useState(null);
    const [ shiftMissed, setShiftMissed ] = React.useState(null);
    const [ timelog, setTimelog ] = React.useState(shift.timelogs?.find(t => t.patroller === patroller.id));

    const shiftName = (!nextDuty ? styles[ shift.name ] : null);
    const shiftAssigned = (assigned ? styles.assigned : null);
    const shiftStarted = (timelog?.started ? styles.started : null);
    const shiftStopped = (timelog?.stopped ? styles.stopped : null);
    const weekendShift = (DateTime.fromMillis(shift.start).weekday === 6 || DateTime.fromMillis(shift.start).weekday === 7);

    const handleAssignShift = (role) => {
        setShowRole(false);

        // TODO: possibly change 'assigned && assigned.id' to just 'assigned'; it might simplify/standardize clearDutyAssignment/setDutyAssignment
        onAssignDuty(shift, assigned, role);
    };

    const handleShowRoster = () => {
        setShowRole(false);
        setShowRoster(!showRoster);
    };

    const handleShowRole = () => {
        setShowRoster(false);
        setShiftAvailable(null);
        setShowRole(!showRole);
    };

    const handleShowTimelog = (s) => {
        setTimelog(showTimelog ?
            shift.timelogs?.find(t => t.patroller === patroller.id) :
            s.timelogs?.find(t => t.patroller === patroller.id));

        setShowRole(false);
        setShowRoster(false);
        setShowTimelog(!showTimelog);
    };

    useInterval(() => {
        try {
            const now = DateTime.local().toMillis();
            const shiftStartTime = shift?.start && DateTime.fromMillis(shift.start).toLocal().toMillis();
            const shiftEndTime = shift?.end && DateTime.fromMillis(shift.end).toLocal().toMillis();
            const shiftStart = now >= shiftStartTime;
            const shiftEnd = now > shiftEndTime;
            const shiftActive = shiftStart && !shiftEnd;

            // Always refresh timelog data, not just when timelog modal is open
            const updatedTimelog = shift.timelogs?.find(t => t.patroller === patroller.id);

            if (updatedTimelog?.id !== timelog?.id || updatedTimelog?.started !== timelog?.started || updatedTimelog?.stopped !== timelog?.stopped) {
                setTimelog(updatedTimelog);
            }

            setShiftAvailable(shift.available > 0 && !shiftEnd);
            setShiftNext(assigned && now < shiftEndTime ? styles.next : null);
            setShiftMissed(assigned && shiftEnd && !timelog?.started && !timelog?.stopped ? styles.missed : null);
            setShiftLate(assigned && shiftActive && !timelog?.started ? styles.late : null);
            setShiftCompleted(timelog?.started && timelog?.stopped ? styles.stopped : null);
            setShiftDisabled((!timelog && !assigned && shiftEnd) || (!assigned && (shiftEnd || !shift.available)) ? styles.disabled : null);
        } catch (error) {
            console.error('Shift useInterval error:', error);

            setShiftAvailable(false);
            setShiftDisabled(styles.disabled);
            setShiftNext(null);
            setShiftLate(null);
        }
    }, 500);

    return (
        <div className={ `${ styles.shiftCard } ${ weekendShift ? styles.weekend : '' } ${ (showRoster || showRole) ? styles.expanded : '' }` }>
            <Grid container columnSpacing={ 1 } display='flex' alignItems='center' sx={ { color: !shift.available ? 'text.disabled' : undefined } } className={ `${ shiftName } ${ shiftDisabled } ${ shiftAssigned } ${ shiftNext } ${ shiftMissed } ${ shiftCompleted } ${ shiftLate } ${ shiftStarted } ${ shiftStopped }` }>
                <Grid size={ 1 } onClick={ handleShowRoster }>
                    <InfoToolTip title={
                        shift.available > 0
                            ? <div>{ shift.roles.filter(r => r.available > 0).map((r, i) => (
                                <div key={ i }>{ r.name }: { r.available }</div>
                            )) }</div>
                            : 'No roles available'
                    } arrow>
                        <Avatar sx={ { width: { xs: 18, sm: 24 }, height: { xs: 18, sm: 24 }, fontSize: { xs: '10px', sm: '12px' }, bgcolor: shift.available > 0 ? "success.main" : "default.main" } }>{ shift.available }</Avatar>
                    </InfoToolTip>
                </Grid>

                <Grid size={ { xs: 1, sm: 2 } } onClick={ handleShowRoster }>
                    <Typography noWrap={ true }>{ shift.name }</Typography>
                </Grid>

                <Grid size={ { xs: 3, sm: 4 } } onClick={ handleShowRoster }>
                    <Typography noWrap={ true }>{ shift.event?.nameShort || 'Unknown Event' }</Typography>
                </Grid>

                { shift?.start && shift?.end && DateTime.fromMillis(shift.start).isValid && DateTime.fromMillis(shift.end).isValid && (
                    <Grid container size={ { xs: 6, sm: 4 } } display='flex' justifyContent='space-between'>
                        <Grid size={ { sm: 6 } } display='flex' direction='row' onClick={ handleShowRoster } gap={ 1 }>
                            <Typography display={ { xs: 'none', sm: 'block' } }>
                                { DateTime.fromMillis(shift.start).toFormat('ccc') }
                            </Typography>

                            <Typography display={ { xs: 'block', sm: 'none' } }>
                                { DateTime.fromMillis(shift.start).toFormat('MM/dd') }
                            </Typography>

                            <Typography display={ { xs: 'none', sm: 'block' } }>
                                { DateTime.fromMillis(shift.start).toFormat('MMM dd') }
                            </Typography>
                        </Grid>

                        <Grid size={ { xs: 3 } } display='flex' justifyContent='flex-end' onClick={ handleShowRoster }>
                            <Typography>{ DateTime.fromMillis(shift.start).toLocaleString(DateTime.TIME_24_SIMPLE) }</Typography>
                        </Grid>

                        <Grid size={ { xs: 3 } } display='flex' justifyContent='flex-end' onClick={ handleShowRoster }>
                            <Typography>{ DateTime.fromMillis(shift.end).toLocaleString(DateTime.TIME_24_SIMPLE) }</Typography>
                        </Grid>
                    </Grid>
                ) }

                <Grid size={ 1 } display='flex' justifyContent='flex-end'>
                    <Alert
                        flexItem
                        shift={ shift }
                        patroller={ patroller }
                        next={ nextDuty }
                        assigned={ assigned }
                        available={ shiftAvailable }
                        late={ shiftLate }
                        started={ shiftStarted }
                        stopped={ shiftStopped }
                        onAssignDuty={ handleShowRole }
                        onShowTimelog={ handleShowTimelog }
                    />
                </Grid>
            </Grid>

            { (showRoster || showRole || showTimelog) && (
                <div className={ styles.expandedContent }>
                    { showRoster ? (
                        <Roster shift={ shift } />
                    ) : showRole ? (
                        <Roles shift={ assigned ? assigned : shift } patroller={ patroller } shiftStatus={ shiftStatus } onRoleSelect={ handleAssignShift } />
                    ) : showTimelog ? (
                        <Timelog shift={ shift } timelog={ timelog } />
                    ) : null }
                </div>
            ) }
        </div>
    );
};